// File: frontend/src/pages/StudentProfilePage.jsx

import React, { useState, useEffect } from 'react';
import { studentPortalAPI } from '../services/api';
import Spinner from '../components/Spinner';
import toast from 'react-hot-toast';

export default function StudentProfilePage() {
    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [phone, setPhone] = useState('');
    const [parentLineId, setParentLineId] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        studentPortalAPI.getMyProfile()
            .then(data => {
                setProfile(data);
                setPhone(data.phone || '');
                setParentLineId(data.parent_line_id || '');
            })
            .catch(() => toast.error('Could not load profile.'))
            .finally(() => setIsLoading(false));
    }, []);

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await studentPortalAPI.updateMyProfile({ phone, parent_line_id: parentLineId });
            toast.success('Profile updated!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not update profile.');
        } finally {
            setSavingProfile(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match.');
            return;
        }
        setChangingPassword(true);
        try {
            await studentPortalAPI.changeMyPassword(currentPassword, newPassword);
            toast.success('Password changed!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not change password.');
        } finally {
            setChangingPassword(false);
        }
    };

    if (isLoading) return <Spinner />;

    return (
        <div className="space-y-6 max-w-lg">
            <h2 className="text-2xl font-bold">My Profile</h2>

            <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-1">{profile.name}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Username: {profile.username}{profile.email ? ` • ${profile.email}` : ''}
                </p>

                <form onSubmit={handleSaveProfile} className="space-y-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                        <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full border rounded-lg p-2 text-sm" placeholder="0912-345-678" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Parent LINE ID (for notifications)</label>
                        <input type="text" value={parentLineId} onChange={e => setParentLineId(e.target.value)} className="w-full border rounded-lg p-2 text-sm" placeholder="e.g. chen_line" />
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">Attendance, leave, and teacher note notifications are sent here via LINE.</p>
                    </div>
                    <button type="submit" disabled={savingProfile} className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg text-sm">
                        {savingProfile ? 'Saving...' : 'Save Changes'}
                    </button>
                </form>
            </div>

            <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-3">Change Password</h3>
                <form onSubmit={handleChangePassword} className="space-y-3">
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Current Password</label>
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full border rounded-lg p-2 text-sm" required />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">New Password</label>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full border rounded-lg p-2 text-sm" required minLength={6} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Confirm New Password</label>
                        <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full border rounded-lg p-2 text-sm" required minLength={6} />
                    </div>
                    <button type="submit" disabled={changingPassword} className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg text-sm">
                        {changingPassword ? 'Changing...' : 'Change Password'}
                    </button>
                </form>
            </div>
        </div>
    );
}
